'use client';

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import type { PageFilterField } from 'elowen-plugin-ui-kit';
import { ArrowLeft, ChevronRight, Download, FileCode, FileJson, Plus, Trash2 } from 'lucide-react';
import { projectName } from 'elowen-plugin-shared/projectChoice';
import { interpolate, useTranslation } from '../../lib/i18n';
import { useSurfaceLabel } from '../../lib/i18n/surfaceLabel';
import { normalizeText } from '../../lib/normalizeText';
import { brainModelQualifiedLabel } from '../../lib/modelProvider';
import { buildConversationTree, filterConversationTree, groupJobLinks, type ConversationRow, type ConversationTreeNode } from '../../lib/conversationTree';
import { useConversationPaging } from '../../lib/useConversationPaging';
import { useMyNavSettings, usePluginUi } from '../../lib/queries';
import { useConversationListPrefs } from './useConversationListPrefs';
import { useNow } from '../../lib/useNow';
import { openBrainSession } from '../../lib/brainDock';
import { parseBrainSessionTarget, PluginHistoryBranchHost } from '../../lib/pluginChatUi';
import { ActionMenu } from '../../components/ui/ActionMenu';
import { Button, buttonClassName } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ConversationStateDot, type ConversationStateTone } from '../../components/ui/ConversationIcon';
import { ModelIcon } from '../../components/ui/ModelIcon';
import { PlatformIcon } from '../../components/ui/PlatformIcon';
import { Pager } from '../../components/ui/Pager';
import { ProjectIcon } from '../../components/ui/ProjectIcon';
import { SelectionActionBar } from '../../components/ui/SelectionActionBar';
import { useGroupFold } from '../../components/ui/SettingsSurface';
import { ScrollArea } from '../../components/ui/shadcn/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/shadcn/collapsible';
import { EmptyState, ErrorState, LoadingLine, LoadingState } from '../../components/ui/states';
import { useMobileViewport } from '../../lib/useMobile';
import { useOperationFailure, useToast } from '../../components/ui/Toast';
import { holdsAnswer, releasedBy, releasing } from '../../lib/saveFeedback';
import { NewConversationMenu } from './NewConversationMenu';
import { ConversationList } from './ConversationList';
import { ConversationListToolbar } from './ConversationListToolbar';
import { useConversationListController } from './useConversationListController';
import {
  EMPTY_CONVERSATION_FILTERS, buildConversationSections, conversationModelKey, conversationProjectKey,
  conversationTypeKeys, countRunningAgents, matchesConversationFilters, pageConversationSections, subagentNodeName,
  type ConversationListActions, type ConversationListExportFormat, type ConversationListFilterOption,
  type ConversationListItem, type ConversationListToolbarProps, type ConversationListView,
} from './conversationListModel';

const BULK_DELETE_KEY = 'conversation-bulk-delete';
const EMPTY_IDS: ReadonlySet<string> = new Set();
/** Root conversations per page. A page is taller than the dialog on purpose and scrolls inside it,
 *  so the pager is a coarse jump rather than the only way through a long history. */
const PAGE_SIZE = 30;

/** Interaction owner for both views. Reads and authorized writes stay in the controller. */
export function ConversationListPanel({ view, onNavigate, homeLink = false, scopeField }: {
  view: ConversationListView;
  onNavigate?: () => void;
  homeLink?: boolean;
  scopeField?: PageFilterField;
}) {
  const { locale, t } = useTranslation();
  const surfaceName = useSurfaceLabel();
  const phone = useMobileViewport() === true;
  const uid = useId();
  const { toast } = useToast();
  const reportFailure = useOperationFailure();
  const [search, setSearch] = useState('');
  const { filters, sort, setFilters, setSort } = useConversationListPrefs(view);
  const [pinnedOpen, setPinnedOpen] = useGroupFold('conversationList.pinned', true);
  const [pageIds, setPageIds] = useState<readonly string[]>([]);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(EMPTY_IDS);
  const [openJobs, setOpenJobs] = useState<ReadonlySet<string>>(EMPTY_IDS);
  const [exportFormat, setExportFormat] = useState<ConversationListExportFormat | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(EMPTY_IDS);
  const [renaming, setRenaming] = useState<ReadonlySet<string>>(EMPTY_IDS);
  // Rows whose delete answer was lost: they may be gone already, so nothing here can select or delete them again
  // until the listing has been read back (the card's reload).
  const [lostDeletes, setLostDeletes] = useState<ReadonlySet<string>>(EMPTY_IDS);
  const [deleteTarget, setDeleteTarget] = useState<ConversationListItem | null>(null);
  const [leaveTarget, setLeaveTarget] = useState<ConversationListItem | null>(null);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [agentsFor, setAgentsFor] = useState<ConversationListItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [pinTarget, setPinTarget] = useState<{ id: string; pinned: boolean } | null>(null);
  const busyRef = useRef(false);
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; };
  }, []);
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectionBarRef = useRef<HTMLDivElement>(null);
  const showSelectionBar = !agentsFor && selected.size > 0;
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const bar = selectionBarRef.current;
    if (!showSelectionBar || !scroll || !bar) return;
    const measure = () => {
      // Measure the wrapped bar and its native bottom inset, independent of its entrance transform.
      const reserve = bar.getBoundingClientRect().height + Number.parseFloat(getComputedStyle(bar).bottom);
      scroll.style.paddingBottom = `${Math.ceil(reserve)}px`;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar, { box: 'border-box' });
    return () => {
      observer.disconnect();
      scroll.style.removeProperty('padding-bottom');
    };
  }, [showSelectionBar]);
  const [page, setPage] = useState(0);
  const [archivePage, setArchivePage] = useState(0);
  const [archiveHeld, setArchiveHeld] = useState<ReadonlySet<string>>(EMPTY_IDS);
  const now = useNow();
  const navSettings = useMyNavSettings();
  const controller = useConversationListController(view, { search, pageIds, onNavigate });
  const { items, itemById, read } = controller;
  const pluginUi = usePluginUi(locale);
  const historyBranch = controller.links?.historyBranch;
  const scheduledLabel = pluginUi.data?.find(entry => entry.name === historyBranch?.plugin)?.strings?.historyBranch
    || t.conversationList.relatedItems;
  const jobs = useMemo(() => groupJobLinks(controller.links?.links ?? []), [controller.links?.links]);
  const tree = useMemo(() => buildConversationTree(items.filter(item => item.archivedAt === null), jobs), [items, jobs]);
  const archiveTree = useMemo(() => buildConversationTree(items.filter(item => item.archivedAt !== null), jobs), [items, jobs]);
  const needle = search.trim().length >= 2 ? normalizeText(search.trim()) : '';
  const hasFacets = Object.values(filters).some(values => values.length > 0);
  const filtered = useMemo(() => filterConversationTree(tree, needle,
    row => !needle || normalizeText(`${row.title} ${row.model} ${row.ownerLabel ?? ''}`).includes(needle) || controller.snippets.has(row.id),
    hasFacets ? row => matchesConversationFilters(itemById.get(row.id)!, filters, jobs.get(row.id)?.length ?? 0) : undefined),
  [tree, needle, controller.snippets, itemById, filters, jobs, hasFacets]);
  const sections = useMemo(() => buildConversationSections(filtered.roots, itemById, view, sort, now, filtered.contextIds, navSettings.data?.pinOrder),
    [filtered.roots, itemById, view, sort, now, filtered.contextIds, navSettings.data?.pinOrder]);
  const roots = useMemo(() => sections.flatMap(section => section.roots), [sections]);
  const archiveFiltered = useMemo(() => filterConversationTree(archiveTree, needle,
    row => !needle || normalizeText(`${row.title} ${row.model} ${row.ownerLabel ?? ''}`).includes(needle) || controller.snippets.has(row.id),
    hasFacets ? row => matchesConversationFilters(itemById.get(row.id)!, filters, jobs.get(row.id)?.length ?? 0) : undefined),
  [archiveTree, needle, controller.snippets, itemById, filters, jobs, hasFacets]);
  const archiveSections = useMemo(() => buildConversationSections(archiveFiltered.roots, itemById, view, sort, now, archiveFiltered.contextIds, navSettings.data?.pinOrder),
    [archiveFiltered.roots, itemById, view, sort, now, archiveFiltered.contextIds, navSettings.data?.pinOrder]);
  const archiveRoots = archiveSections.flatMap(section => section.roots);
  const archiveClamped = Math.min(archivePage, Math.max(0, Math.ceil(archiveRoots.length / PAGE_SIZE) - 1));
  const archivePageSections = pageConversationSections(archiveSections, archiveClamped, PAGE_SIZE);
  useLayoutEffect(() => {
    if (!pinTarget) return;
    const item = itemById.get(pinTarget.id);
    // Wait for the committed projection, then follow its current section/sort order, not the old page.
    if (item && (item.pinnedAt !== null) !== pinTarget.pinned) return;
    const index = roots.findIndex(root => root.row.id === pinTarget.id);
    if (index !== -1) setPage(Math.floor(index / PAGE_SIZE));
    setPinTarget(null);
  }, [pinTarget, itemById, roots, setPage]);
  const facetKey = JSON.stringify(filters);
  const { clampedPage, pageRows } = useConversationPaging(roots, page, PAGE_SIZE, setPage, setPageIds, search, sort.key, sort.direction, facetKey);
  const pageSections = pageConversationSections(sections, clampedPage, PAGE_SIZE);
  // A folded pinned section hides its rows, so neither "select page" nor a kept selection may reach them.
  const foldedIds = new Set(pinnedOpen ? [] : pageSections.find(section => section.key === 'pinned')?.roots.map(node => node.row.id));
  const pageSelectableIds = pageRows.map(node => node.row.id).filter(id => {
    const item = itemById.get(id)!;
    return !filtered.contextIds.has(id) && !renaming.has(id) && !foldedIds.has(id) && !lostDeletes.has(id) && (item.capabilities.delete || item.capabilities.export);
  });
  const selectable = new Set(pageSelectableIds);
  const selectedOnPage = pageSelectableIds.filter(id => selected.has(id)).length;
  const pageSelection = selectedOnPage === 0 ? false : selectedOnPage === pageSelectableIds.length ? true : 'indeterminate';
  const selectionKey = JSON.stringify([search, facetKey, sort, clampedPage]);
  useEffect(() => {
    if (busyRef.current) return;
    setSelected(EMPTY_IDS);
  }, [selectionKey]);
  const visibleKey = JSON.stringify(pageSelectableIds);
  useEffect(() => {
    if (busyRef.current) return;
    const ids = new Set(JSON.parse(visibleKey) as string[]);
    setSelected(current => {
      const next = new Set([...current].filter(id => ids.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [visibleKey, busy]);

  // Keep labels for selected facets whose last row disappeared in a refetch.
  const optionCache = useRef<ConversationListToolbarProps['options']>({ projects: [], statuses: [], models: [], types: [], owners: [] });
  const options = useMemo<ConversationListToolbarProps['options']>(() => {
    const projects = new Map<string, ConversationListFilterOption>();
    const models = new Map<string, ConversationListFilterOption>();
    const types = new Map<string, ConversationListFilterOption>();
    const owners = new Map<string, ConversationListFilterOption>();
    for (const item of items) {
      const project = conversationProjectKey(item);
      const shownProject = item.project ?? item.projectIdentity;
      projects.set(project, { value: project, label: item.project ? projectName(item.project)
        : item.projectIdentity ? item.projectIdentity.name
          : item.projectUnavailable ? t.conversationList.filters.unavailableProject
            : item.projectRef?.projectId !== undefined ? read.projects.status === 'error' ? t.conversationList.projectLoadError : t.common.loading
              : t.conversationList.filters.noProject,
      icon: shownProject ? <ProjectIcon project={shownProject} size={14} /> : undefined });
      const model = conversationModelKey(item);
      models.set(model, { value: model, label: brainModelQualifiedLabel({ provider: item.provider ?? '', model: item.model }),
        icon: <ModelIcon name={item.model} size={14} /> });
      for (const type of conversationTypeKeys(item, jobs.get(item.id)?.length ?? 0)) {
        // Scheduled runs share the cron glyph; a bare channel kind has no platform to draw.
        const platform = type === 'scheduled' ? 'cron' : type === 'web' || type === item.platform ? type : undefined;
        types.set(type, { value: type, label: type === 'web' ? t.conversationList.filters.webCli
          : type === 'scheduled' ? t.conversationList.filters.scheduled : surfaceName(type),
        // The item text already names the type, so the platform mark stays out of the accessible name.
        icon: platform ? <span aria-hidden className="inline-flex shrink-0"><PlatformIcon platform={platform} size={14} /></span> : undefined });
      }
      owners.set(String(item.ownerId), { value: String(item.ownerId), label: item.ownerLabel ?? String(item.ownerId) });
    }
    const next = {
      projects: [...projects.values()], models: [...models.values()], types: [...types.values()], owners: [...owners.values()],
      statuses: ([
        ['working', t.conversationList.filters.working, 'working'],
        ['waiting', t.conversationList.filters.waiting, 'input'],
        ['unread', t.conversationList.filters.unread, 'free'],
        ['error', t.conversationList.filters.error, 'error'],
      ] as const satisfies readonly (readonly [string, string, ConversationStateTone])[]).map(([value, label, tone]) => ({ value, label,
        icon: <span className="inline-flex size-3.5 shrink-0 items-center justify-center"><ConversationStateDot tone={tone} /></span> })),
    };
    for (const key of ['projects', 'models', 'types', 'owners'] as const) {
      const selectedValues = filters[key].map(String);
      for (const previous of optionCache.current[key]) {
        if (selectedValues.includes(previous.value) && !next[key].some(option => option.value === previous.value)) next[key].push(previous);
      }
      next[key].sort((a, b) => a.label.localeCompare(b.label));
    }
    optionCache.current = next;
    return next;
  }, [items, jobs, filters, t, surfaceName, read.projects.status]);

  const toggle = (id: string) => {
    if (busyRef.current) return;
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const select = (id: string) => {
    if (busyRef.current || !selectable.has(id)) return;
    setSelectionMode(true);
    setSelected(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectPage = () => {
    if (busyRef.current) return;
    setSelected(current => {
      const next = new Set(current);
      for (const id of pageSelectableIds) {
        if (pageSelection === true) next.delete(id); else next.add(id);
      }
      return next;
    });
  };
  const renameChange = (id: string, editing: boolean) => {
    setRenaming(current => {
      const next = new Set(current);
      if (editing) next.add(id); else next.delete(id);
      return next;
    });
    if (editing) setSelected(current => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  };
  // Runs one operation under the panel's busy lock. A rejection reaches the caller.
  const locked = async (operation: () => Promise<boolean>): Promise<boolean> => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    try { return await operation(); }
    finally {
      busyRef.current = false;
      if (live.current) setBusy(false);
    }
  };
  // An inline action has no dialog to own its promise, so the panel reports its refusal.
  const execute = async (operation: () => Promise<boolean>, errorMessage: string): Promise<boolean> => {
    try { return await locked(operation); }
    catch {
      if (live.current) toast(errorMessage, 'error');
      return false;
    }
  };
  // A confirmed operation: the dialog awaits this promise, reports its rejection once and stays open.
  const confirmed = async (operation: () => Promise<boolean>, close: () => void): Promise<void> => {
    if (await locked(operation) && live.current) close();
  };
  const actions: ConversationListActions = {
    open: item => { if (!busyRef.current) controller.open(item.id); },
    rename: (item, title) => execute(() => controller.rename(item.id, title), t.chat.renameError),
    fork: item => { void execute(() => controller.fork(item.id), t.chat.forkError); },
    archiveHeld: id => archiveHeld.has(id),
    archive: item => {
      if (archiveHeld.has(item.id)) return;
      void (async () => {
        try { await locked(() => controller.setArchived(item.id, item.archivedAt === null)); }
        catch (error) {
          if (!live.current) return;
          if (holdsAnswer(error)) setArchiveHeld(current => new Set([...current, item.id]));
          const release = () => setArchiveHeld(current => { const next = new Set(current); next.delete(item.id); return next; });
          reportFailure(`conversation-archive:${item.id}`, releasedBy(error, release), { message: t.brainChat.archiveFailed,
            reload: releasing(controller.reload, release) });
        }
      })();
    },
    mute: controller.mute,
    mutePending: controller.mutePending,
    pin: item => { void execute(async () => {
      const pinned = item.pinnedAt === null;
      const success = await controller.setPinned(item.id, pinned);
      if (success && live.current) {
        if (pinned) setPinnedOpen(true);
        setPinTarget({ id: item.id, pinned });
      }
      return success;
    }, t.chat.pinError); },
    export: (item, format) => { void execute(() => controller.exportSession(item.id, format), t.chat.exportError); },
    delete: item => { if (!busyRef.current && !lostDeletes.has(item.id)) setDeleteTarget(item); },
    leave: item => { if (!busyRef.current) setLeaveTarget(item); },
    showSubagents: item => { if (!busyRef.current) setAgentsFor(item); },
  };
  const bulk = async (kind: 'delete' | ConversationListExportFormat) => {
    if (busyRef.current) return;
    const ids = [...selected].filter(id => {
      const item = itemById.get(id);
      return item && !lostDeletes.has(id) && (kind === 'delete' ? item.capabilities.delete : item.capabilities.export);
    });
    if (!ids.length) return;
    busyRef.current = true;
    setBusy(true);
    setConfirmBulkDelete(false);
    const failed = new Set<string>();
    // A row whose answer was lost may be gone already: it is neither counted as done nor kept selected, so no
    // second delete can reach it before the listing has been read again.
    const unknown = new Set<string>();
    let lost: unknown;
    let started = 0;
    try {
      if (kind === 'delete') ids.sort((a, b) => Number(a === controller.activeId) - Number(b === controller.activeId));
      for (const id of ids) {
        if (!live.current) return;
        try {
          const success = await (kind === 'delete' ? controller.deleteSession(id) : controller.exportSession(id, kind));
          if (!success || !live.current) return;
          started += 1;
        } catch (error) { if (kind === 'delete' && holdsAnswer(error)) { unknown.add(id); lost ??= error; } else failed.add(id); }
      }
      if (!live.current) return;
      setSelected(failed);
      if (kind !== 'delete') toast(interpolate(t.conversationList.exportPartial, { started, failed: failed.size }), failed.size ? 'error' : 'ok');
      else if (unknown.size) {
        // ONE card for the batch. Rows with a lost answer are held out of every selection and delete until the
        // listing is read again; the rows that failed plainly stay selected, and the ones that went are gone.
        setLostDeletes(current => new Set([...current, ...unknown]));
        reportFailure(BULK_DELETE_KEY, lost, { reload: releasing(controller.reload, () => setLostDeletes(EMPTY_IDS)) });
      } else if (failed.size) toast(started === 0 ? t.brainChat.bulkDeleteFailed
        : interpolate(t.brainChat.bulkDeletePartial, { done: started, failed: failed.size }), 'error');
    } finally {
      busyRef.current = false;
      if (live.current) setBusy(false);
    }
  };
  /** A sub-agent of `delegatedFrom` opens read-only, named by that conversation and by the name its
   *  sub-agent tree lists it under. */
  const openTarget = (value: string, delegatedFrom?: ConversationRow) => {
    if (busyRef.current) return;
    const target = parseBrainSessionTarget(value);
    if (!target) return;
    openBrainSession(target.sessionId, delegatedFrom ? false : target.continuable, delegatedFrom ? {
      delegated: true, title: delegatedFrom.title,
      name: subagentNodeName(controller.links?.subagents?.[delegatedFrom.id] ?? [], target.sessionId),
    } : {});
    onNavigate?.();
  };
  const toggleJobs = (id: string) => {
    if (busyRef.current) return;
    setOpenJobs(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Mine lists no delegated sessions as rows (All nests them as children), so its branch draws the
  // conversation's sub-agent tree the row menu already reads.
  const subagentBranch = (node: ConversationTreeNode): ReactNode => view === 'mine' && subagentIds.has(node.row.id)
    ? <PluginHistoryBranchHost plugin="subagent" branch="subagent" parent={node.row}
      items={controller.links?.subagents?.[node.row.id] ?? []} expanded toggle={() => toggle(node.row.id)}
      open={value => openTarget(value, node.row)}
      fallback={<p className="p-3 text-meta text-muted-foreground">{t.subagentBranch.unavailable}</p>} /> : null;
  const jobsBranch = (node: ConversationTreeNode): ReactNode => {
    if (!node.jobs.length) return null;
    const expanded = openJobs.has(node.row.id) || filtered.jobsExpanded.has(node.row.id);
    const fallback = <div className="min-w-0 py-1">
      <Button type="button" variant="ghost" size="sm" onClick={() => toggleJobs(node.row.id)}
        aria-label={interpolate(t.conversationList.relatedItemsToggle, { title: node.row.title || t.brainChat.untitled })}
        aria-controls={expanded ? `${uid}-jobs-${encodeURIComponent(node.row.id)}` : undefined}
        aria-expanded={expanded}>
        <ChevronRight size={12} aria-hidden /><span>{t.conversationList.relatedItems}</span><span>{node.jobs.length}</span>
      </Button>
      {expanded ? <ul id={`${uid}-jobs-${encodeURIComponent(node.row.id)}`} className="space-y-1 pl-4">
        {node.jobs.map(job => <li key={job.jobId}>{job.run
          ? <Button type="button" variant="ghost" size="sm"
            onClick={() => { openBrainSession(job.run!.sessionId, job.run!.continuable); onNavigate?.(); }}>{job.name}</Button>
          : <Link href={job.href} onClick={onNavigate} className="flex items-center gap-1.5 text-small text-muted-foreground hover:text-foreground">{job.name}</Link>}</li>)}
      </ul> : null}
    </div>;
    return historyBranch ? <PluginHistoryBranchHost plugin={historyBranch.plugin} branch={historyBranch.key}
      parent={node.row} items={node.jobs} expanded={expanded} toggle={() => toggleJobs(node.row.id)}
      open={value => openTarget(value)} fallback={fallback} /> : fallback;
  };
  const branch = (node: ConversationTreeNode): ReactNode => <>{subagentBranch(node)}{jobsBranch(node)}</>;
  const runningAgents = new Map<string, { count: number; truncated: boolean }>();
  const subagentIds = new Set<string>();
  if (read.subagents.status === 'ready') for (const item of itemById.values()) {
    const nodes = controller.links?.subagents?.[item.id] ?? [];
    runningAgents.set(item.id, countRunningAgents(nodes, item.programStatus?.state === 'working' ? item.id : undefined));
    if (nodes.length) subagentIds.add(item.id);
  }
  const scheduledCounts = read.jobs.status === 'ready' ? new Map([...jobs].map(([id, links]) => [id, links.length])) : undefined;
  const errors: ReactNode[] = [];
  for (const [key, message] of [
    ['sessions', t.conversationList.listLoadError], ['projects', t.conversationList.projectLoadError],
    ['jobs', t.conversationList.relatedItemsError], ['subagents', t.subagentBranch.error],
    ...(view === 'all' ? [['personal', t.conversationList.personalLoadError]] as const : []),
    ['search', t.brainChat.searchError],
  ] as const) {
    const state = read[key];
    if (state.status === 'error') errors.push(<ErrorState key={key} message={message} onRetry={state.retry} />);
  }
  const agentsView = agentsFor ? <div className="flex min-h-0 min-w-0 flex-1 flex-col">
    <Button type="button" variant="ghost" size="sm" className="self-start" icon={ArrowLeft} onClick={() => setAgentsFor(null)}>{t.common.back}</Button>
    {read.subagents.status === 'error' ? <ErrorState message={t.subagentBranch.error} onRetry={read.subagents.retry} /> : null}
    {read.subagents.status === 'loading' ? <LoadingLine /> : null}
    <PluginHistoryBranchHost plugin="subagent" branch="subagent" parent={agentsFor}
      items={controller.links?.subagents?.[agentsFor.id] ?? []} expanded toggle={() => setAgentsFor(null)}
      open={value => openTarget(value, agentsFor)}
      fallback={<p className="p-3 text-meta text-muted-foreground">{t.subagentBranch.unavailable}</p>} />
  </div> : null;

  const matchingCount = sections.reduce((total, section) => total + section.total, 0);
  const listHeader = <>
    <ConversationListToolbar scopeField={scopeField} view={view} search={search} onSearch={value => { if (!busyRef.current) setSearch(value); }}
      filters={filters} onFilters={value => { if (!busyRef.current) setFilters(typeof value === 'function' ? value(filters) : value); }}
      options={options} sort={sort} onSort={value => { if (!busyRef.current) setSort(value); }}
      selectionMode={selectionMode} onSelectionMode={() => { if (!busyRef.current) setSelectionMode(true); }}
      pageSelection={pageSelection} onSelectPage={selectPage} busy={busy}
      newConversation={<NewConversationMenu onChosen={onNavigate}><Button type="button" variant="default" size={phone ? "icon" : "default"} icon={Plus} disabled={busy} aria-label={t.conversationList.new} title={t.conversationList.new}>{phone ? null : t.conversationList.new}</Button></NewConversationMenu>}
      deleteAll={view === 'all' && controller.isAdmin ? <Button type="button" variant="ghost" size="sm" icon={Trash2} disabled={busy || lostDeletes.size > 0} onClick={() => { if (lostDeletes.size === 0) setConfirmAll(true); }}>{t.sessionsPanel.deleteAll}</Button> : undefined} />
    {homeLink ? <Link href="/dash" onClick={onNavigate} className="flex items-center gap-2 border-b border-border px-2 py-2 text-ui text-muted-foreground hover:bg-accent hover:text-foreground">
      <ArrowLeft size={16} aria-hidden />{t.nav.dashboard}
    </Link> : null}
    {errors}
    {needle && read.search.status === 'loading' ? <LoadingLine label={t.conversationList.searchLoading} /> : null}
    {needle ? <p className="px-3 py-1 text-small text-muted-foreground">{t.conversationList.searchBounded}</p> : null}
    <p role="status" className="sr-only">{interpolate(t.conversationList.resultCount, { count: matchingCount })}</p>
  </>;
  return <>{agentsView}<section className={`relative min-h-0 min-w-0 flex-1 flex-col ${agentsFor ? 'hidden' : 'flex'}`} data-conversation-view={view}>
    {/* Phones scroll the controls with the records; wider screens retain their fixed toolbar. */}
    {!phone ? listHeader : null}
    {/* The shared ScrollArea shows the bar only while the list is in use (the app's one scrollbar rule).
        It bleeds into the body's right padding so the bar runs in that gutter, clear of the cards, while
        the cards keep the toolbar's right edge. */}
    <ScrollArea className="-mr-4 min-h-0 min-w-0 flex-1 sm:-mr-6">
      <div ref={scrollRef} data-testid="conversation-list-scroll" className="pr-4 sm:pr-6" aria-busy={read.sessions.status === 'loading' || undefined}>
        {phone ? <div className="pb-3">{listHeader}</div> : null}
        {read.sessions.status === 'loading' && !items.length ? <LoadingState /> : roots.length ? <ConversationList
          sections={pageSections} items={itemById} view={view} activeId={controller.activeId} selected={selected}
          selectionMode={selectionMode} busy={busy} contextIds={filtered.contextIds} heldIds={lostDeletes}
          expanded={new Set([...expanded, ...filtered.expanded])} snippets={controller.snippets} search={search} now={now}
          pinnedOpen={pinnedOpen} onPinnedOpenChange={setPinnedOpen}
          actions={actions} onToggle={toggle} onSelect={select} onRenameChange={renameChange} renderBranch={branch}
          runningAgents={runningAgents} scheduledCounts={scheduledCounts} scheduledLabel={scheduledLabel} subagentIds={subagentIds} />
          : read.sessions.status === 'ready' && read.search.status === 'ready'
            ? <EmptyState title={needle || facetKey !== JSON.stringify(EMPTY_CONVERSATION_FILTERS) ? t.sessionsPanel.noMatches : t.chat.emptyHistory} /> : null}
        <Collapsible>
          <CollapsibleTrigger className="group/fold flex items-center gap-2 rounded-sm px-3 py-2 text-small text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">
            {t.conversationList.archived} <span className="tabular-nums">{archiveRoots.length}</span>
            <ChevronRight size={14} aria-hidden className="transition-transform group-data-[state=open]/fold:rotate-90" />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ConversationList sections={archivePageSections} items={itemById} view={view} activeId={controller.activeId} selected={EMPTY_IDS}
              selectionMode={false} selectionEnabled={false} busy={busy} contextIds={archiveFiltered.contextIds} expanded={new Set([...expanded, ...archiveFiltered.expanded])}
              snippets={controller.snippets} search={search} now={now} pinnedOpen={true} onPinnedOpenChange={() => {}}
              actions={actions} onToggle={toggle} onSelect={() => {}} renderBranch={branch} scheduledLabel={scheduledLabel} />
            {archiveRoots.length ? <Pager page={archiveClamped} pageSize={PAGE_SIZE} total={archiveRoots.length} ariaLabel={t.conversationList.archived} onPageChange={setArchivePage} /> : null}
          </CollapsibleContent>
        </Collapsible>
      </div>
    </ScrollArea>
    {roots.length ? <fieldset disabled={busy} className="contents"><Pager page={clampedPage} pageSize={PAGE_SIZE} total={roots.length} ariaLabel={t.chat.historyTitle}
      onPageChange={value => { if (!busyRef.current) setPage(value); }} /></fieldset> : null}
    {showSelectionBar ? <SelectionActionBar ref={selectionBarRef} count={interpolate(t.brainChat.selectedCount, { n: selected.size })}
      clearLabel={t.brainChat.clearSelection} disabled={busy} onClear={() => { if (!busyRef.current) { setSelected(EMPTY_IDS); setSelectionMode(false); } }}>
      <div title={t.conversationList.exportSeparateFiles}>
        {/* The export menu is a labelled trigger on the bar's own row, so it takes the shared default step
            like the Delete button beside it — its box, its padding and its coarse floor. It used to state a
            content-height trigger of its own, which left 28px beside 36px and no touch floor at all. */}
        <ActionMenu variant="kebab" label={t.conversationList.exportSelected} trigger={<><Download size={16} aria-hidden /><span>{t.conversationList.exportSelected}</span></>}
          triggerClassName={buttonClassName('ghost', 'default', 'gap-2')}
          items={[
            { label: t.chat.exportHtml, icon: FileCode, disabled: busy, onSelect: () => {}, onAfterClose: () => setExportFormat('html') },
            { label: t.chat.exportJsonl, icon: FileJson, disabled: busy, onSelect: () => {}, onAfterClose: () => setExportFormat('jsonl') },
          ]} />
      </div>
      <Button type="button" variant="default" icon={Trash2} disabled={busy} onClick={() => setConfirmBulkDelete(true)}>{t.brainChat.bulkDelete}</Button>
    </SelectionActionBar> : null}
    {selected.size ? <p className="sr-only">{t.conversationList.exportSeparateFiles}</p> : null}
    <ConfirmDialog open={deleteTarget !== null} title={t.brainChat.deleteChatConfirmTitle}
      description={deleteTarget ? interpolate(t.brainChat.deleteChatConfirmDescription, { title: deleteTarget.title || t.brainChat.untitled }) : undefined}
      confirmLabel={t.brainChat.deleteChat} pending={busy} failureMessage={() => t.brainChat.deleteChatError} reload={controller.reload}
      onConfirm={() => deleteTarget ? confirmed(() => controller.deleteSession(deleteTarget.id), () => setDeleteTarget(null)) : undefined}
      onClose={() => setDeleteTarget(null)} />
    <ConfirmDialog open={leaveTarget !== null} title={t.chat.leaveConfirm} description={t.chat.leaveConfirmBody}
      confirmLabel={t.chat.leaveConversation} pending={busy} failureMessage={() => t.chat.participantChangeFailed} reload={controller.reload}
      onConfirm={() => leaveTarget ? confirmed(() => controller.leave(leaveTarget.id), () => setLeaveTarget(null)) : undefined}
      onClose={() => setLeaveTarget(null)} />
    <ConfirmDialog open={confirmBulkDelete} title={t.brainChat.bulkDeleteConfirmTitle}
      description={interpolate(t.brainChat.bulkDeleteConfirmBody, { n: selected.size })} confirmLabel={t.brainChat.bulkDelete}
      onConfirm={() => bulk('delete')} onClose={() => setConfirmBulkDelete(false)} />
    <ConfirmDialog open={exportFormat !== null} title={t.conversationList.exportSelected}
      description={t.conversationList.exportSeparateFiles} confirmLabel={t.conversationList.exportSelected} confirmVariant="default"
      onConfirm={async () => { if (exportFormat) { const format = exportFormat; setExportFormat(null); await bulk(format); } }}
      onClose={() => setExportFormat(null)} />
    <ConfirmDialog open={confirmAll} title={t.sessionsPanel.confirmDeleteAllTitle} description={t.conversationList.deleteAllScope}
      confirmLabel={t.sessionsPanel.deleteAll} pending={busy} failureMessage={() => t.common.error} reload={controller.reload}
      onConfirm={() => confirmed(controller.deleteAll, () => setConfirmAll(false))}
      onClose={() => setConfirmAll(false)} />
  </section></>;
}
